import * as React from "react";
import { Check, Clock, Minus, X } from "lucide-react";

import { cn } from "@/lib/utils";

// The approval journey shown as a timeline at the top of a detail dialog
// (RequestDetailDialog / AdvanceDetailDialog) - Requestor → Approver →
// Finance approver, each node a glowing status dot coloured by its own
// outcome. Horizontal with a gradient-blended rail from sm up; a vertical
// rail on phones (the three labelled nodes don't fit side-by-side on a narrow
// screen - same reasoning as WizardStepper's two layouts).
//
// The detail API only resolves the REQUESTER's display name today; approver
// records carry just an id (see requests_api.py's get_travel_request /
// get_advance). So each node is labelled by its ROLE, with a name shown only
// where the caller actually has one to pass - never a raw id.

export type TimelineState =
  | "done"
  | "approved"
  | "rejected"
  | "pending"
  | "skipped";

export interface TimelineNode {
  /** Role label shown above the name - "Requestor", "Approver", … */
  role: string;
  /** Resolved display name, when the caller has one; otherwise a short
   *  fallback ("-", "Finance team", …). */
  name: string;
  state: TimelineState;
  /** Badge text under the node ("Submitted", "Approved", "Rejected", …). */
  statusLabel: string;
}

// Colour + icon per node state, from the app's theme tokens (index.css) -
// the same green / amber / red the status badges and dashboard charts use,
// so a stage reads the same colour everywhere and follows light/dark mode.
// `token` is the bare HSL triplet variable name: the rail gradient and the
// dot's glow need it as a value (with alpha), which a static Tailwind class
// can't express for an arbitrary pair, so it's applied via inline style.
const STATE_STYLES: Record<
  TimelineState,
  {
    token: string;
    text: string;
    icon: React.ComponentType<{ className?: string }>;
  }
> = {
  done: { token: "--primary", text: "--primary-foreground", icon: Check },
  approved: { token: "--primary", text: "--primary-foreground", icon: Check },
  rejected: {
    token: "--destructive",
    text: "--destructive-foreground",
    icon: X
  },
  pending: { token: "--quadra-orange", text: "--foreground-on-amber", icon: Clock },
  skipped: { token: "--muted-foreground", text: "--background", icon: Minus }
};

const fill = (token: string, alpha = 1) =>
  alpha === 1 ? `hsl(var(${token}))` : `hsl(var(${token}) / ${alpha})`;
const textColour = (token: string) =>
  // Amber is light in both themes, so its text stays black (same as the
  // amber status badges' text-black) rather than following a theme token.
  token === "--foreground-on-amber" ? "#000" : fill(token);

function Dot({ state, size }: { state: TimelineState; size: number }) {
  const style = STATE_STYLES[state];
  const Icon = style.icon;
  return (
    <div
      className="flex items-center justify-center rounded-full ring-1 ring-background"
      style={{
        // rem, so the dot follows the text-size setting with its icon.
        height: `${size / 16}rem`,
        width: `${size / 16}rem`,
        backgroundColor: fill(style.token),
        color: textColour(style.text),
        // Two-stop halo: a tight bright ring plus a softer outer bloom.
        boxShadow: `0 0 0 3px ${fill(style.token, 0.2)}, 0 0 12px 1px ${fill(style.token, 0.6)}`
      }}
    >
      <Icon className="h-4 w-4" />
    </div>
  );
}

function StatusBadge({
  state,
  label
}: {
  state: TimelineState;
  label: string;
}) {
  const style = STATE_STYLES[state];
  return (
    <span
      className="inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium shadow-sm"
      style={{
        backgroundColor: fill(style.token),
        color: textColour(style.text)
      }}
    >
      {label}
    </span>
  );
}

export function ApprovalTimeline({ nodes }: { nodes: TimelineNode[] }) {
  if (nodes.length === 0) return null;
  return (
    <div className="rounded-xl border bg-card p-5">
      {/* Horizontal (sm+). The rails are a separate absolutely-positioned
          layer BEHIND the dots rather than flex items between them - an
          in-flow rail can never reliably reach the dots, because each dot is
          centred in its own equal-width (flex-1) column and so has a half-
          column of empty space on either side that the rail would float in.
          With N equal columns, column i's centre is at (i+0.5)/N of the
          width, so a rail from dot i to dot i+1 starts there and spans exactly
          1/N - touching both dot centres. The dots (z-10) paint over the ends,
          which tuck under them. */}
      <div className="relative hidden sm:block">
        <div
          className="pointer-events-none absolute inset-x-0"
          style={{ top: 30 / 2 - 3 / 2 }}
          aria-hidden
        >
          {nodes.slice(0, -1).map((node, i) => (
            <div
              key={node.role}
              className="absolute h-[3px] rounded-full"
              style={{
                left: `${((i + 0.5) / nodes.length) * 100}%`,
                width: `${(1 / nodes.length) * 100}%`,
                backgroundImage: `linear-gradient(to right, ${fill(STATE_STYLES[node.state].token)}, ${fill(STATE_STYLES[nodes[i + 1].state].token)})`
              }}
            />
          ))}
        </div>
        <div className="flex items-start">
          {nodes.map((node) => (
            <div
              key={node.role}
              className="relative z-10 flex flex-1 flex-col items-center text-center"
            >
              <Dot state={node.state} size={30} />
              <p className="mt-2.5 text-[0.6875rem] font-medium uppercase tracking-wide text-muted-foreground">
                {node.role}
              </p>
              <p className="mt-0.5 max-w-[10rem] truncate text-sm font-semibold">
                {node.name}
              </p>
              <div className="mt-2">
                <StatusBadge state={node.state} label={node.statusLabel} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Vertical (phones) */}
      <ol className="flex flex-col sm:hidden">
        {nodes.map((node, i) => {
          const next = nodes[i + 1];
          const isLast = i === nodes.length - 1;
          return (
            <li key={node.role} className="flex gap-3.5">
              <div className="flex flex-col items-center">
                <Dot state={node.state} size={36} />
                {!isLast && next && (
                  <div
                    className="my-1 w-[3px] flex-1 rounded-full"
                    style={{
                      backgroundImage: `linear-gradient(to bottom, ${fill(STATE_STYLES[node.state].token)}, ${fill(STATE_STYLES[next.state].token)})`
                    }}
                  />
                )}
              </div>
              <div className={cn("min-w-0", isLast ? "pb-0" : "pb-5")}>
                <p className="text-[0.6875rem] font-medium uppercase tracking-wide text-muted-foreground">
                  {node.role}
                </p>
                <p className="truncate text-sm font-semibold">{node.name}</p>
                <div className="mt-1.5">
                  <StatusBadge state={node.state} label={node.statusLabel} />
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
